import { useContext, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import sanitizeHtml from "sanitize-html";

import casesService from "api/casesService";
import Button from "components/Button";
import SearchMemberInWorkspace from "components/SearchMemberInWorkspace";
import TextArea from "components/TextArea";
import CasesContext from "contexts/CasesContext";
import { IWorkspaceMember } from "dto/IWorkspace";
import { getErrorMessage } from "utils/getErrorMessage";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

// Przekazanie teczki (teczek) innemu pracownikowi — port „Przekaż" z Magistratu. Jedno
// żądanie POST /case/pass z CaseIds: BE zmienia właściciela WSZYSTKICH albo ŻADNEJ (atomowo),
// więc po sukcesie można bezpiecznie zdjąć teczki z listy nadawcy. Bez przeszyfrowywania
// kluczy per odbiorca (dawny handleRedirect/reEncrypt z ery kluczy profilowych) — odbiorca
// czyta teczkę kluczem firmy.

const PassCasesModal: FC<Props> = ({ cases, onClose, onPassed }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { caseId } = useParams();
  const { cases: casesContext, setCases: setCasesContext } =
    useContext(CasesContext);

  const [receiver, setReceiver] = useState<IWorkspaceMember | null>(null);
  const [comment, setComment] = useState("");
  const { mutate: passCases, isLoading } = casesService.usePassCases();

  const handlePass = () => {
    if (!receiver || !cases.length || isLoading) return;

    passCases(
      {
        CaseIds: cases.map((c) => c.Id),
        ReceiverId: receiver.UserId,
        Comment: comment.trim() || undefined,
      },
      {
        onSuccess: (response) => {
          const passedIds = response?.data?.PassedCaseIds ?? [];
          const passedSet = new Set(passedIds);

          // Nadawca przestaje być właścicielem — teczki znikają z jego listy (chyba że
          // dalej ma do nich pełny dostęp przez wspólny kod JRWA; wtedy wrócą po odświeżeniu).
          setCasesContext(casesContext.filter((c) => !passedSet.has(c.Id)));

          notification({
            type: ENotificationType.SUCCESS,
            title: t("cases.passTitle"),
            text: t("cases.passSuccess", {
              count: passedIds.length,
              receiver: receiver.Label,
            }),
          });

          if (caseId && passedSet.has(caseId)) {
            navigate("/cases");
          }

          onPassed?.(passedIds);
          onClose();
        },
        onError: (error) => {
          notification({
            type: ENotificationType.ERROR,
            title: t("cases.passTitle"),
            text: getErrorMessage(error) || t("cases.passError"),
          });
        },
      },
    );
  };

  return ReactDOM.createPortal(
    <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
      <div className={styles.wrapper}>
        <div className={styles.title}>
          {cases.length === 1
            ? t("cases.passTitle")
            : t("cases.passTitleMany", { count: cases.length })}
        </div>

        <div className={styles.list}>
          {cases.map((c) => (
            <div key={c.Id} className={styles.case}>
              <div
                className={styles.signature}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(c.Signature ?? ""),
                }}
              />
              <div
                className={styles.caseTitle}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(c.Title ?? ""),
                }}
              />
            </div>
          ))}
        </div>

        <div className={styles.field}>
          <SearchMemberInWorkspace
            label={t("cases.passReceiver")}
            showMe={false}
            onChoose={(member) => setReceiver(member)}
          />
        </div>

        <div className={styles.field}>
          <TextArea
            label={t("cases.passComment")}
            value={comment}
            onChange={(text) => setComment(text)}
            minRows={2}
          />
        </div>

        <div className={styles.hint}>{t("cases.passHint")}</div>

        <div className={styles.buttonsWrapper}>
          <div onClick={onClose} className={styles.cancel}>
            {t("common.cancel")}
          </div>
          <Button
            className={styles.confirm}
            onClick={handlePass}
            disabled={!receiver || isLoading}
            loading={isLoading}
            text={
              cases.length === 1
                ? t("cases.passConfirm")
                : t("cases.passConfirmMany", { count: cases.length })
            }
          />
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PassCasesModal;
